import type { DataRecordDetailComponent, DataRecordField } from "../../types";
import { getSharedComponentRegistry, setSharedComponentRegistry, type SharedComponentRenderer } from "../sharedRegistry";
import { isParagraphHtml } from "../../utils/paragraphHtml";
import { paragraphHtmlToSafeHtml } from "../../utils/inlineIcons";
import { DataRecordPdfPreview } from "./DataRecordPdfPreview";
import { PageTitle } from "./PageTitle";
import { VideoEmbedRenderer } from "./VideoEmbedRenderer";
import { StatusBadge } from "../../components/activities/StatusBadge";

/**
 * Generic detail block for a single dated data record.
 *
 * One component covers every BCC-Radar data type - rate values, FX rates,
 * multi-field statistics rows, PDF-bearing publications - so the dynamic-path
 * deploy engine can render any `page.json` it materialises with a single
 * authored template.
 *
 * The component is authored once as a template page: `recordDate`, every
 * `fields[].value`, `pdfKey` and `body` carry `{token}` placeholders that the
 * deploy engine substitutes per record. A record missing a field leaves the
 * literal `{token}` behind - so at render time any value that is still an
 * unsubstituted token (or empty) is dropped: empty field rows are skipped, and
 * an unresolved `pdfKey`/`body` is treated as absent.
 *
 * Shared `PageTitle` header + record date meta line + two-column field table +
 * PDF preview + rich-text body. The title reuses the canonical `PageTitle`
 * component (the same primary-blue `#005CA9` Aleo `<h1>` with a yellow
 * `#FFA401` underline that `PageHeaderRenderer` renders) so a migrated record
 * page reads identically to an authored page. The content renders directly on
 * the white page shell - exactly like an editorial `PageHeader` + `RichText`
 * page - with no surrounding card chrome; the page shell already supplies the
 * container and padding. Colours follow the site system exactly - primary blue
 * `#005CA9` for headings and field values, body navy `#0F2849`, muted
 * blue-grey `#6B7A99` for the record-date / field-label / source meta text,
 * `slate-100` for the subtle field-row dividers. The PDF preview reuses the
 * same iframe pattern (`#view=Fit&zoom=75`, download + open-in-new-tab links)
 * used by the admin file picker so the preview is identical everywhere.
 */

/** True when the value is empty or still an unsubstituted `{token}` placeholder. */
function isUnresolved(value?: string | null): boolean {
  const trimmed = (value ?? "").trim();
  if (!trimmed) return true;
  return /^\{.*\}$/.test(trimmed);
}

/** Keeps only field rows with a real, substituted value. */
function visibleFields(fields: DataRecordField[] | undefined): DataRecordField[] {
  return (fields ?? []).filter((f) => f && !isUnresolved(f.value));
}

/**
 * True when a field value is purely numeric (digits + separators) - rates,
 * amounts, percentages. Numbers read best right-aligned with tabular figures;
 * text values (addresses, names) read best left-aligned.
 */
function isNumericValue(value: string): boolean {
  return /^[\d\s.,%+\-/()]+$/.test(value.trim());
}

/**
 * True when a field carries a video `embedUrl` - the `videos` content type's
 * `embedUrl` field. Matched by label (the field's authored title "URL embed",
 * or the raw key `embedUrl`), so a `videos` record renders an inline player
 * instead of a flat URL table row.
 */
function isVideoField(field: DataRecordField): boolean {
  const label = (field.label ?? "").trim().toLowerCase();
  return label === "url embed" || label === "embedurl" || label === "url embed video";
}

/**
 * True when a field is the record's own title - it is already rendered as the
 * page heading (`PageTitle`), so repeating it as a "TITRE" metadata row below
 * the description is pure duplication. Matched by the `title` label or by a
 * value identical to the heading.
 */
function isTitleField(field: DataRecordField, headingTitle: string): boolean {
  const label = (field.label ?? "").trim().toLowerCase();
  if (label === "titre" || label === "title") return true;
  const value = (field.value ?? "").trim();
  return value.length > 0 && value === (headingTitle ?? "").trim();
}

/**
 * Record bodies render their non-RichText items through the shared injected
 * registry (see `../sharedRegistry` for the anti-cycle rationale). Kept as an
 * alias of {@link setSharedComponentRegistry} so existing callers that wired
 * the record-body registry directly keep working unchanged. When the registry
 * is absent (leaf unit tests), non-RichText body components render nothing.
 */
export function setRecordBodyComponentRegistry(registry: Record<string, SharedComponentRenderer>) {
	setSharedComponentRegistry(registry);
}

/**
 * Migrated record body: an array of page components instead of the legacy
 * HTML string. A `RichText` item carries the legacy HTML verbatim and MUST
 * render byte-identically to the old string path (same wrapper classes, same
 * sanitizer) - that parity is what makes the body-to-components migration
 * visually invisible. Any other component type renders through the shared
 * registry, so an editor can enrich a record body with real widgets.
 */
export function RecordBodyComponents({ components }: { components: Array<{ componentType?: string; content?: any }> }) {
  const items = (components ?? []).filter((c) => c && typeof c.componentType === "string");
  if (items.length === 0) return null;
  return (
    <>
      {items.map((item, index) => {
        if (item.componentType === "RichText") {
          const html = (item.content?.html ?? "").trim();
          if (!html) return null;
          return isParagraphHtml(html) ? (
            <div
              key={index}
              className="rich-paragraph-block mb-6 text-base text-[#0F2849] [&_a]:font-semibold [&_a]:text-[#005CA9] [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-[#0F2849]"
              dangerouslySetInnerHTML={{ __html: paragraphHtmlToSafeHtml(html) }}
            />
          ) : (
            <p key={index} className="mb-6 text-base text-[#0F2849]">{html}</p>
          );
        }
        const renderer = getSharedComponentRegistry()?.[item.componentType as string];
        if (!renderer) return null;
        return <div key={index} className="mb-6">{renderer(item)}</div>;
      })}
    </>
  );
}

/** Renderer for the generic dated-record detail block. */
export function DataRecordDetailRenderer({ component }: { component: DataRecordDetailComponent }) {
  const { title, typeLabel, recordDate, fields, pdfKey, body, sourceLabel, hideTitle, statusBadge } = component.content;
  // Per-record title (e.g. "Note de conjoncture hebdomadaire du 17 avril
  // 2026") wins over the shared content-type label when authored. Empty
  // or unresolved `{title}` tokens fall back to typeLabel so the page
  // never shows a literal token or a blank <h1>.
  const headingTitle = title && !isUnresolved(title) ? title.trim() : typeLabel;

  // A `videos` record carries its `embedUrl` as a field - lift it out of the
  // flat field table and render it as an inline 16:9 player instead of a row.
  const resolvedFields = visibleFields(fields);
  const videoField = resolvedFields.find(isVideoField);
  const rows = resolvedFields.filter((f) => f !== videoField && !isTitleField(f, headingTitle));
  const hasPdf = !isUnresolved(pdfKey);
  const hasBody = typeof body === "string" && !isUnresolved(body);
  const dateText = isUnresolved(recordDate) ? "" : recordDate.trim();

  // L'enrichisseur record-detail injecte désormais un PageHeader « sujet »
  // (titre + fil d'Ariane) en tête de page : quand il le fait, il pose
  // `hideTitle` ici pour que le bloc ne rende pas un second H1. La ligne de
  // date reste - elle appartient au corps du record, pas à l'en-tête.
  const showHeading = hideTitle !== true && String(hideTitle) !== "true";

  return (
    <article>
      {/* Canonical site title (reuses PageTitle) + muted record-date meta. La
          pastille de statut juridique (C.68), quand l'enrichisseur l'a posée,
          partage la ligne de date ; sans date, elle occupe la ligne seule. */}
      {showHeading ? <PageTitle title={headingTitle} /> : null}
      {dateText || statusBadge?.label ? (
        <div
          className={`${showHeading ? "-mt-1" : "mt-0"} mb-4 flex flex-wrap items-center gap-x-3 gap-y-1`}
        >
          {dateText ? (
            <p className="text-sm font-medium text-[#6B7A99] md:text-base">{dateText}</p>
          ) : null}
          {statusBadge?.label ? <StatusBadge badge={statusBadge} /> : null}
        </div>
      ) : null}

      {/* Description / rich-text body - directly below the title + date, so a
          publication reads title → date → description → document. Dual-read:
          migrated bodies are a components array (RichText carries the legacy
          HTML verbatim and MUST render byte-identically to the old string
          path); pre-migration bodies remain the plain HTML/text string. */}
      {Array.isArray(body) ? (
        <RecordBodyComponents components={body} />
      ) : hasBody ? (
        isParagraphHtml(body as string) ? (
          <div
            /* MÊMES classes que la branche composants ci-dessus : le test de
               parité corps-string / corps-composants compare le DOM à l'octet. */
            className="rich-paragraph-block mb-6 text-base text-[#0F2849] [&_a]:font-semibold [&_a]:text-[#005CA9] [&_a]:underline [&_a]:underline-offset-2 [&_a:hover]:text-[#0F2849]"
            dangerouslySetInnerHTML={{ __html: paragraphHtmlToSafeHtml(body as string) }}
          />
        ) : (
          <p className="mb-6 text-base text-[#0F2849]">{(body as string).trim()}</p>
        )
      ) : null}

      {/* Stacked field list - label above value. The two-column table layout
          made long comma-separated values (taxonomy lists, free-text fields)
          read like a list of fake links; stacking them as a definition list
          gives long values room to wrap and lets each label/value pair read as
          a clean metadata block. */}
      {rows.length > 0 ? (
        <dl className="mb-6 mt-2 divide-y divide-slate-100 border-y border-slate-100">
          {rows.map((field, index) => (
            <div
              key={`${field.label}-${index}`}
              className="py-3.5 first:pt-4 last:pb-4 md:py-4"
            >
              <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-[#6B7A99] md:text-sm">
                {field.label}
              </dt>
              <dd
                className={
                  "mt-2 text-base font-medium leading-relaxed text-[#005CA9] md:text-lg " +
                  (isNumericValue(field.value) ? "tabular-nums" : "")
                }
              >
                {field.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/* Video embed - a `videos` record stores its player URL in a field;
          render it as a responsive 16:9 player rather than a flat URL row.
          VideoEmbedRenderer normalises the URL and self-hides on an empty /
          unresolved value, so a non-video record shows nothing. */}
      {videoField ? (
        <div className="mt-6">
          <VideoEmbedRenderer
            component={{
              componentType: "VideoEmbed",
              content: { embedUrl: videoField.value },
            }}
          />
        </div>
      ) : null}

      {/* PDF preview - same iframe behaviour as the rest of the site.
          `pdfKey` is a bucket-relative storage key; DataRecordPdfPreview is a
          client component that resolves it through the runtime `assetResolver`
          (env-driven) into a browser-loadable absolute URL - exactly how
          images resolve via `imageComponent`. It self-hides when the resolved
          URL is empty, so an unresolvable key shows nothing. */}
      {hasPdf ? <DataRecordPdfPreview pdfKey={pdfKey as string} /> : null}

      {/* Optional footer source note. */}
      {sourceLabel && !isUnresolved(sourceLabel) ? (
        <p className="mt-6 border-t border-slate-100 pt-3 text-xs italic text-[#6B7A99]">
          {sourceLabel.trim()}
        </p>
      ) : null}
    </article>
  );
}
